import { CasMetadataNode } from './CasMetadataNode';
import { CPMetadataFilingTypes } from '../../js/interfaces/CoverpathTypes';
import { ApplicationState } from './ApplicationState';
import { amorphicStatic } from '@haventech/amorphic';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathBasePolicy } from '../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { GiactUtils } from './../../../common/js/GiactUtils';
import { ValidationService } from '../services/validation/validation.service';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

export enum ApplicationSectionsList {
  CHRONIC_ILLNESS_RIDER = 'Chronic Illness Rider',
  CASE_SETUP = 'Case Setup',
  CASE_DETAILS = 'Case Details',
  PAYMENT_METHOD = 'Payment Method',
  VERIFY = 'Verify',
  SOURCE_OF_FUNDS = 'Source of Funds',
}

const ApplicationSections2021FilingTeleInterviewer = [
  ApplicationSectionsList.CASE_DETAILS,
  'Insured Information',
  'Medical',
];

const ApplicationSections2020FilingTeleInterviewer = [
  ApplicationSectionsList.CASE_DETAILS,
  'Insured Information',
  'Medical Information',
];

const ApplicationSections2021Filing = [
  ApplicationSectionsList.CASE_SETUP,
  'Insured Information',
  'Owner Information',
  'Medical',
  'Purpose',
  'Beneficiaries',
  'Existing Policies',
  'Policy Information',
  'Chronic Illness Rider',
  'Acknowledgements',
  ApplicationSectionsList.SOURCE_OF_FUNDS,
  'Payment Method',
  'Verify',
];

const ApplicationSections2020Filing = [
  ApplicationSectionsList.CASE_SETUP,
  'Insured Information',
  'Owner & Employment Information',
  'Medical Information',
  'Purpose of Insurance',
  'Beneficiary Information',
  'Existing Policies',
  'Policy Information',
  'Acknowledgements',
  ApplicationSectionsList.SOURCE_OF_FUNDS,
  'Payment',
  'Verify',
];

const ApplicationSections2019Filing = [
  'Insured Information',
  'Owner Information',
  'Medical',
  'Purpose',
  'Beneficiaries',
  'Existing Policies',
  'Policy Information',
  'Acknowledgements',
  'Payment',
  'Verify',
];

const ApplicationSections2018Filing = [
  'Basics',
  'Background',
  'Family History',
  'Personal History',
  'Lifestyle',
  'Purpose',
  'Beneficiaries',
  'Existing Policies',
  'Policy Information',
  'Acknowledgements',
  'Payment',
  'Verify',
];

export class ApplicationSectionUtils {
  /**
   *  If it is the last section, it will return the last section.
   */
  public static getNextSection(section: number, cpFilingType: string, appState: ApplicationState): number {
    let nextSection = section + 1;
    const allSections = ApplicationSectionUtils.getAllSections(cpFilingType, appState);
    if (this.shouldSkipSection(nextSection, cpFilingType, appState)) {
      nextSection++;
    }
    return Math.min(nextSection, allSections.length - 1);
  }

  /**
   * If it is the first section, it will return the first section.
   */
  public static getPreviousSection(section: number, cpFilingType: string, appState: ApplicationState): number {
    let previousSection = section - 1;
    if (this.shouldSkipSection(previousSection, cpFilingType, appState)) {
      previousSection--;
    }
    return Math.max(previousSection, 0);
  }

  public static shouldSkipSection(sectionNumber: number, cpFilingType: string, appState: ApplicationState): boolean {
    const sectionName = this.getSectionNameByNumber(sectionNumber, cpFilingType, appState);
    const primaryPolicy = appState.controller.customer.application.primaryPolicy;
    const flags = {
      mmGiactWebDebitVerificationServiceEnabled: appState.controller.mmGiactWebDebitVerificationServiceEnabled,
    };

    return this.shouldDisableSection(primaryPolicy, sectionName, flags);
  }
  /**
   * returns the correct array of section names based on whether the application version
   */
  public static getAllSections(cpFilingType: string, appState: ApplicationState): string[] {
    if (appState.isTeleInterviewerLoggedIn) {
      switch (cpFilingType) {
        case CPMetadataFilingTypes.CP2021:
          return ApplicationSections2021FilingTeleInterviewer;
        default:
          return ApplicationSections2020FilingTeleInterviewer;
      }
    }

    const primaryPolicy = appState.controller.customer.application.primaryPolicy;

    if (
      (this.shouldHideCaseSetup(appState) ||
        this.shouldHideVerify(appState) ||
        this.shouldHideCir(appState) ||
        ApplicationSectionUtils.shouldDisableSection(primaryPolicy, ApplicationSectionsList.SOURCE_OF_FUNDS)) &&
      cpFilingType === CPMetadataFilingTypes.CP2021
    ) {
      let sectionList = ApplicationSections2021Filing;
      if (this.shouldHideCaseSetup(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.CASE_SETUP);
      }
      if (this.shouldHideVerify(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.VERIFY);
      }
      if (this.shouldHideCir(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.CHRONIC_ILLNESS_RIDER);
      }

      if (ApplicationSectionUtils.shouldDisableSection(primaryPolicy, ApplicationSectionsList.SOURCE_OF_FUNDS)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.SOURCE_OF_FUNDS);
      }
      return sectionList;
    } else if (cpFilingType === CPMetadataFilingTypes.CP2021) {
      return ApplicationSections2021Filing;
    } else if (
      (this.shouldHideCaseSetup(appState) ||
        this.shouldHideVerify(appState) ||
        this.shouldHideCir(appState) ||
        ApplicationSectionUtils.shouldDisableSection(primaryPolicy, ApplicationSectionsList.SOURCE_OF_FUNDS)) &&
      cpFilingType === CPMetadataFilingTypes.CP2020
    ) {
      let sectionList = ApplicationSections2020Filing;
      if (this.shouldHideCaseSetup(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.CASE_SETUP);
      }
      if (this.shouldHideVerify(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.VERIFY);
      }
      if (this.shouldHideCir(appState)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.CHRONIC_ILLNESS_RIDER);
      }
      if (ApplicationSectionUtils.shouldDisableSection(primaryPolicy, ApplicationSectionsList.SOURCE_OF_FUNDS)) {
        sectionList = sectionList.filter((section) => section !== ApplicationSectionsList.SOURCE_OF_FUNDS);
      }
      return sectionList;
    } else if (cpFilingType === CPMetadataFilingTypes.CP2020) {
      return ApplicationSections2020Filing;
    } else if (cpFilingType === CPMetadataFilingTypes.CP2019) {
      return ApplicationSections2019Filing;
    } else {
      return ApplicationSections2018Filing;
    }
  }

  public static getSectionNumberByName(name: string, cpFilingType: string, appState: ApplicationState): number {
    const sectionList = ApplicationSectionUtils.getAllSections(cpFilingType, appState);
    return sectionList.indexOf(name);
  }

  public static getSectionNameByNumber(
    sectionNumber: number,
    cpFilingType: string,
    appState: ApplicationState
  ): string {
    const sectionList = ApplicationSectionUtils.getAllSections(cpFilingType, appState);
    return sectionList[sectionNumber];
  }

  public static isQuestionItemInCurrentSection(
    questionItem: CasMetadataNode,
    currentSection: number,
    cpFilingType: string,
    appState: ApplicationState
  ): boolean {
    const questionSection: number = ApplicationSectionUtils.getSectionNumberByName(
      questionItem.section,
      cpFilingType,
      appState
    );
    return currentSection === questionSection;
  }

  public static shouldHideCir(appState: ApplicationState): boolean {
    const cirNode = appState.appMetadata.children.filter(
      (node) => node.section === ApplicationSectionsList.CHRONIC_ILLNESS_RIDER
    )[0];
    if (!cirNode) {
      return true;
    }
    return !ApplicationSectionUtils.doesSectionHaveQuestions(cirNode);
  }

  public static shouldHideCaseSetup(appState: ApplicationState): boolean {
    const caseSetupNode =
      appState.appMetadata.children.filter((node) => node.section === ApplicationSectionsList.CASE_SETUP)[0] ||
      appState.removedSections[ApplicationSectionsList.CASE_SETUP];
    if (!caseSetupNode) {
      return true;
    }

    return (
      appState.isTeleInterviewerLoggedIn ||
      !appState.isAdvisor ||
      !ApplicationSectionUtils.doesSectionHaveQuestions(caseSetupNode)
    );
  }

  public static shouldHideVerify(appState: ApplicationState): boolean {
    const verifyNode =
      appState.appMetadata.children.filter((node) => node.section === ApplicationSectionsList.VERIFY)[0] ||
      appState.removedSections[ApplicationSectionsList.VERIFY];
    if (!verifyNode) {
      return true;
    }

    return appState.isAdvisor && !ApplicationSectionUtils.doesSectionHaveQuestions(verifyNode);
  }

  public static filterEmptySections(appState) {
    for (const child of appState.appMetadata.children) {
      const hasQuestions = ApplicationSectionUtils.doesSectionHaveQuestions(child);

      // No questions are shown in this section, therefore we should hide it
      if (!hasQuestions) {
        ApplicationSectionUtils.filterSection(child.section, appState);
      }
    }
  }

  public static doesSectionHaveQuestions(section) {
    let nodeStack = [section];
    let hasQuestions = false;
    while (nodeStack.length > 0) {
      const currentNode = nodeStack.pop();
      if (currentNode.children.length) {
        nodeStack = nodeStack.concat(currentNode.children);
      }

      // topLevel = node is a section node
      if (!currentNode.topLevel) {
        hasQuestions = currentNode.isShown();
      }

      if (currentNode.id === '-1') {
        hasQuestions = false;
      }

      if (hasQuestions) {
        break;
      }
    }

    return hasQuestions;
  }

  public static filterSection(sectionName: string, appState) {
    const nodeIndex = appState.appMetadata.children.findIndex((node) => node.section === sectionName);

    if (nodeIndex < 0) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'ApplicationSectionUtils',
          function: 'filterSection',
          data: {
            section: sectionName,
          },
          category: UICategory.request,
        },
        `Trying to remove ${sectionName} section, but it does not exist in the metadata`
      );
      return;
    }

    appState.removedSections[sectionName] = appState.appMetadata.children[nodeIndex];
    appState.appMetadata.children.splice(nodeIndex, 1);
  }

  public static shouldDisableSection(
    primaryPolicy: CoverpathBasePolicy,
    sectionName: string,
    flags: any = {}
  ): boolean {
    switch (sectionName) {
      case ApplicationSectionsList.CHRONIC_ILLNESS_RIDER:
        return !primaryPolicy.isCoverpathPolicy() || !(primaryPolicy as CoverpathPolicy).hasCirRider;
      case ApplicationSectionsList.SOURCE_OF_FUNDS:
        return !primaryPolicy.isCoverpathPolicy() || primaryPolicy.appMetadataVersion < 172;
      case ApplicationSectionsList.VERIFY:
        return (
          !!flags.mmGiactWebDebitVerificationServiceEnabled &&
          GiactUtils.isInvalidPayment(
            primaryPolicy.isGiactWebDebitValid,
            primaryPolicy.giactWebDebitResponseCode,
            flags.mmGiactWebDebitVerificationServiceEnabled
          )
        );
      default:
        return false;
    }
  }

  public static shouldHideDueToTeleCMI(sectionName: string, appState: ApplicationState, cpFilingType: string): boolean {
    if (appState.isTeleInterviewerLoggedIn) {
      const sectionList = this.getAllSections(cpFilingType, appState);
      return !sectionList.includes(sectionName);
    } else {
      return sectionName === ApplicationSectionsList.CASE_DETAILS;
    }
  }

  public static shouldErrorForPayment(primaryPolicy: CoverpathBasePolicy, sectionName: string) {
    const isPaymentInApplication = primaryPolicy.paymentInApplication;
    const paymentInfo = {
      paymentABA: primaryPolicy.paymentABA,
      paymentAccount: primaryPolicy.paymentAccount,
    };
    const isAccountRoutingInfoSame = ValidationService.isAccountRoutingInfoSame(paymentInfo);

    return (
      (sectionName === ApplicationSectionsList.PAYMENT_METHOD || sectionName === 'Payment') &&
      isPaymentInApplication === 'now' &&
      (primaryPolicy.isGiactWebDebitValid === false || isAccountRoutingInfoSame)
    );
  }
}
